import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    IndianRupee, Users, Store, Package, ShoppingCart,
    TrendingUp, AlertCircle, ArrowRight, Loader2,
    Clock, CheckCircle, XCircle, Truck, BarChart3,
    Bell, Shield, Megaphone, TicketCheck, Settings,
    UserCheck, Star, Inbox
} from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';

const AdminDashboard = () => {
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalRevenue: 0,
        totalCustomers: 0,
        totalVendors: 0,
        totalProducts: 0,
        totalOrders: 0,
        pendingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
    });
    const [orderBreakdown, setOrderBreakdown] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [pendingVendors, setPendingVendors] = useState([]);
    const [topVendors, setTopVendors] = useState([]);
    const [recentAnnouncements, setRecentAnnouncements] = useState([]);
    const [recentTickets, setRecentTickets] = useState([]);

    const isInitialLoad = useRef(true);

    const fetchDashboardData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        try {
            const [ordersRes, productsRes, vendorsRes] = await Promise.all([
                axios.get(`${API}/api/admin/orders`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/api/products`).catch(() => ({ data: [] })),
                axios.get(`${API}/api/vendors`, { headers }).catch(() => ({ data: [] }))
            ]);

            const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
            const products = Array.isArray(productsRes.data) ? productsRes.data : [];
            const vendors = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];

            // Order counts by status
            const orderCounts = {
                pending: orders.filter(o => o.status === 'pending').length,
                confirmed: orders.filter(o => o.status === 'confirmed').length,
                assigned: orders.filter(o => o.status === 'assigned').length,
                processing: orders.filter(o => o.status === 'processing').length,
                shipped: orders.filter(o => o.status === 'shipped').length,
                delivered: orders.filter(o => o.status === 'delivered' || o.status === 'completed').length,
                cancelled: orders.filter(o => o.status === 'cancelled').length,
            };

            const totalRevenue = orders
                .filter(o => o.status !== 'cancelled')
                .reduce((sum, o) => sum + (o.total_amount || 0), 0);

            const customerIds = new Set(orders.map(o => o.user_id).filter(Boolean));

            setStats({
                totalRevenue,
                totalCustomers: customerIds.size,
                totalVendors: vendors.length,
                totalProducts: products.length,
                totalOrders: orders.length,
                pendingOrders: orderCounts.pending,
                completedOrders: orderCounts.delivered,
                cancelledOrders: orderCounts.cancelled,
            });

            setOrderBreakdown([
                { status: 'Pending', count: orderCounts.pending, color: 'bg-yellow-500', icon: Clock },
                { status: 'Confirmed', count: orderCounts.confirmed, color: 'bg-blue-500', icon: CheckCircle },
                { status: 'Assigned', count: orderCounts.assigned, color: 'bg-indigo-500', icon: UserCheck },
                { status: 'Processing', count: orderCounts.processing, color: 'bg-purple-500', icon: Package },
                { status: 'Shipped', count: orderCounts.shipped, color: 'bg-cyan-500', icon: Truck },
                { status: 'Delivered', count: orderCounts.delivered, color: 'bg-green-500', icon: CheckCircle },
                { status: 'Cancelled', count: orderCounts.cancelled, color: 'bg-red-500', icon: XCircle },
            ]);

            setRecentOrders(orders.slice(0, 6));

            // Pending vendor approvals
            const pending = vendors.filter(v => v.status === 'pending');
            setPendingVendors(pending.map(v => ({
                id: v.id,
                name: v.business_name || v.name,
                email: v.email,
                date: v.created_at ? new Date(v.created_at).toLocaleDateString() : 'N/A'
            })));

            // Top vendors by revenue from orders
            const vendorMap = {};
            orders.forEach(order => {
                const vid = order.assigned_vendor_id || order.vendor_id;
                const vname = order.assigned_vendor_name || order.vendor_name;
                if (vid && vname && order.status !== 'cancelled') {
                    if (!vendorMap[vid]) vendorMap[vid] = { name: vname, orders: 0, revenue: 0 };
                    vendorMap[vid].orders += 1;
                    vendorMap[vid].revenue += (order.total_amount || 0);
                }
            });
            setTopVendors(Object.values(vendorMap).sort((a, b) => b.revenue - a.revenue).slice(0, 5));

        } catch (error) {
            console.error('Dashboard fetch error:', error);
        }

        // Fetch announcements
        try {
            const annRes = await axios.get(`${API}/api/announcements`, { headers });
            setRecentAnnouncements((annRes.data || []).slice(0, 3));
        } catch { setRecentAnnouncements([]); }

        // Fetch tickets
        try {
            const ticketRes = await axios.get(`${API}/api/admin/tickets`, { headers });
            setRecentTickets((ticketRes.data || []).slice(0, 4));
        } catch { setRecentTickets([]); }

        if (!silent) setLoading(false);
    }, [token]);

    useEffect(() => {
        if (!token) return;
        fetchDashboardData();
        isInitialLoad.current = false;
        const interval = setInterval(() => fetchDashboardData(true), 30000);
        return () => clearInterval(interval);
    }, [token, fetchDashboardData]);

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-700',
            confirmed: 'bg-blue-100 text-blue-700',
            processing: 'bg-purple-100 text-purple-700',
            assigned: 'bg-indigo-100 text-indigo-700',
            shipped: 'bg-cyan-100 text-cyan-700',
            delivered: 'bg-green-100 text-green-700',
            completed: 'bg-green-100 text-green-700',
            cancelled: 'bg-red-100 text-red-700',
        };
        return styles[status] || 'bg-gray-100 text-gray-700';
    };

    const getTicketStatusColor = (status) => {
        const colors = {
            open: 'bg-blue-100 text-blue-700',
            in_progress: 'bg-yellow-100 text-yellow-700',
            resolved: 'bg-green-100 text-green-700',
            closed: 'bg-gray-100 text-gray-700',
        };
        return colors[status] || 'bg-gray-100 text-gray-700';
    };

    const EmptyState = ({ icon: Icon, title, description }) => (
        <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                <Icon className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium text-sm">{title}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
    );

    if (loading) {
        return (
            <DashboardLayout userRole="admin">
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading dashboard...</p>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Hero Header */}
                <div className="bg-gradient-to-r from-primary via-primary/90 to-primary/80 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />

                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                                <Shield className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold">Welcome back, {user?.name || 'Admin'}!</h1>
                                <p className="text-white/80 flex items-center gap-2 mt-1">
                                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                    Admin Dashboard • Platform Overview
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/admin/announcements">
                                <Button variant="ghost" className="text-white hover:bg-white/20">
                                    <Megaphone className="w-4 h-4 mr-2" />Announcements
                                </Button>
                            </Link>
                            <Link to="/admin/orders">
                                <Button className="bg-white text-primary hover:bg-white/90">
                                    <ShoppingCart className="w-4 h-4 mr-2" />Manage Orders
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Revenue */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <IndianRupee className="w-5 h-5 text-green-600" />
                            </div>
                            {stats.totalRevenue > 0 && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            )}
                        </div>
                        <p className="text-2xl font-bold">₹{stats.totalRevenue.toLocaleString('en-IN')}</p>
                        <p className="text-sm text-muted-foreground mt-1">Total Revenue</p>
                    </motion.div>

                    {/* Total Orders */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                            </div>
                            {stats.pendingOrders > 0 && (
                                <Badge className="bg-yellow-100 text-yellow-700 text-xs">{stats.pendingOrders} pending</Badge>
                            )}
                        </div>
                        <p className="text-2xl font-bold">{stats.totalOrders}</p>
                        <p className="text-sm text-muted-foreground mt-1">Total Orders</p>
                    </motion.div>

                    {/* Products */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-purple-600" />
                            </div>
                            {stats.totalProducts > 0 && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">{stats.totalProducts} listed</Badge>
                            )}
                        </div>
                        <p className="text-2xl font-bold">{stats.totalProducts}</p>
                        <p className="text-sm text-muted-foreground mt-1">Products</p>
                    </motion.div>

                    {/* Vendors */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Store className="w-5 h-5 text-orange-600" />
                            </div>
                            {pendingVendors.length > 0 && (
                                <Badge className="bg-orange-100 text-orange-700 text-xs">{pendingVendors.length} pending</Badge>
                            )}
                        </div>
                        <p className="text-2xl font-bold">{stats.totalVendors}</p>
                        <p className="text-sm text-muted-foreground mt-1">Vendors</p>
                    </motion.div>
                </div>

                {/* Revenue Summary + Order Breakdown */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Revenue Summary */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <IndianRupee className="w-5 h-5 text-primary" />
                                Revenue Summary
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Earnings</p>
                                    <p className="text-2xl font-bold text-green-600">₹{stats.totalRevenue.toLocaleString('en-IN')}</p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <TrendingUp className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Completed Orders</p>
                                    <p className="font-semibold mt-1">{stats.completedOrders}</p>
                                </div>
                                <div className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Customers</p>
                                    <p className="font-semibold mt-1">{stats.totalCustomers}</p>
                                </div>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="text-xs text-muted-foreground">Average Order Value</p>
                                <p className="font-semibold mt-1">
                                    ₹{stats.totalOrders > 0
                                        ? Math.round(stats.totalRevenue / (stats.totalOrders - stats.cancelledOrders || 1)).toLocaleString('en-IN')
                                        : '0'}
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Order Status Breakdown */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-primary" />
                                Order Status
                            </h3>
                            <Link to="/admin/orders">
                                <Button variant="ghost" size="sm">View All</Button>
                            </Link>
                        </div>

                        {stats.totalOrders === 0 ? (
                            <EmptyState
                                icon={Inbox}
                                title="No orders yet"
                                description="Orders will appear here when placed"
                            />
                        ) : (
                            <div className="space-y-3">
                                {orderBreakdown.filter(item => item.count > 0).map((item) => (
                                    <div key={item.status} className="flex items-center gap-3">
                                        <div className={`w-8 h-8 ${item.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                                            <item.icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">{item.status}</span>
                                                <span className="text-sm font-bold">{item.count}</span>
                                            </div>
                                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${item.color} rounded-full transition-all duration-500`}
                                                    style={{ width: `${(item.count / stats.totalOrders) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Top Vendors */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-green-500" />
                                Top Vendors
                            </h3>
                            <Link to="/admin/vendors">
                                <Button variant="ghost" size="sm">View All</Button>
                            </Link>
                        </div>
                        {topVendors.length === 0 ? (
                            <EmptyState
                                icon={Store}
                                title="No vendor sales yet"
                                description="Vendor performance data will appear here"
                            />
                        ) : (
                            <div className="space-y-3">
                                {topVendors.map((vendor, index) => (
                                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-primary/60'
                                            }`}>
                                            {index + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{vendor.name}</p>
                                            <p className="text-xs text-muted-foreground">{vendor.orders} order{vendor.orders !== 1 ? 's' : ''}</p>
                                        </div>
                                        <p className="font-semibold text-sm text-green-600">₹{vendor.revenue.toLocaleString('en-IN')}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Recent Orders + Pending Vendors */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Recent Orders */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border"
                    >
                        <div className="p-5 border-b flex items-center justify-between">
                            <h3 className="font-semibold flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5 text-primary" />
                                Recent Orders
                            </h3>
                            <Link to="/admin/orders">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {recentOrders.length === 0 ? (
                            <div className="p-6">
                                <EmptyState icon={ShoppingCart} title="No orders yet" description="Recent orders will appear here" />
                            </div>
                        ) : (
                            <div className="divide-y">
                                {recentOrders.map((order) => (
                                    <div key={order.id} className="p-4 hover:bg-muted/30 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center font-semibold text-primary flex-shrink-0">
                                                {(order.customer?.name || 'C')[0].toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-medium text-sm truncate">{order.customer?.name || 'Customer'}</p>
                                                    <Badge className={getStatusBadge(order.status)}>{order.status}</Badge>
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    Order #{String(order.id || '').substring(0, 8).toUpperCase()} • {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="font-semibold">₹{(order.total_amount || 0).toLocaleString('en-IN')}</p>
                                                <p className="text-xs text-muted-foreground">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Pending Vendor Approvals */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border"
                    >
                        <div className="p-5 border-b flex items-center justify-between">
                            <h3 className="font-semibold flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-orange-500" />
                                Pending Approvals
                                {pendingVendors.length > 0 && (
                                    <Badge className="bg-orange-100 text-orange-700 text-xs ml-1">{pendingVendors.length}</Badge>
                                )}
                            </h3>
                            <Link to="/admin/vendors">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {pendingVendors.length === 0 ? (
                            <div className="p-6">
                                <EmptyState icon={CheckCircle} title="All caught up!" description="No pending vendor approvals" />
                            </div>
                        ) : (
                            <div className="divide-y">
                                {pendingVendors.slice(0, 5).map((vendor) => (
                                    <div key={vendor.id} className="p-4 hover:bg-muted/30 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <Store className="w-5 h-5 text-orange-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{vendor.name}</p>
                                                <p className="text-xs text-muted-foreground truncate">{vendor.email}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <Badge variant="outline" className="text-xs">Pending</Badge>
                                                <p className="text-xs text-muted-foreground mt-1">{vendor.date}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Announcements + Support Tickets */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Recent Announcements */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:col-span-2 bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold flex items-center gap-2">
                                <Bell className="w-5 h-5 text-primary" />
                                Recent Announcements
                            </h3>
                            <Link to="/admin/announcements">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {recentAnnouncements.length === 0 ? (
                            <EmptyState icon={Megaphone} title="No announcements" description="Create announcements to notify vendors and customers" />
                        ) : (
                            <div className="space-y-3">
                                {recentAnnouncements.map((item, index) => (
                                    <Link
                                        key={index}
                                        to="/admin/announcements"
                                        className={`block p-4 rounded-lg border-l-4 hover:shadow-sm transition-shadow ${item.type === 'success' ? 'bg-green-50 border-green-500' :
                                            item.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                                                item.type === 'error' || item.type === 'urgent' ? 'bg-red-50 border-red-500' :
                                                    'bg-blue-50 border-blue-500'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">{item.title}</p>
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{item.message}</p>
                                            </div>
                                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-4">
                                                {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Quick Actions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <h3 className="font-semibold mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <Link to="/admin/orders" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium text-sm block">Manage Orders</span>
                                    <span className="text-xs text-muted-foreground">{stats.pendingOrders} pending</span>
                                </div>
                            </Link>
                            <Link to="/admin/vendors" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Store className="w-5 h-5 text-orange-600" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium text-sm block">Manage Vendors</span>
                                    <span className="text-xs text-muted-foreground">{pendingVendors.length} awaiting approval</span>
                                </div>
                            </Link>
                            <Link to="/admin/products" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Package className="w-5 h-5 text-purple-600" />
                                </div>
                                <span className="font-medium text-sm">View Products</span>
                            </Link>
                            <Link to="/admin/announcements" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Megaphone className="w-5 h-5 text-green-600" />
                                </div>
                                <span className="font-medium text-sm">Announcements</span>
                            </Link>
                            <Link to="/admin/tickets" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                                    <TicketCheck className="w-5 h-5 text-cyan-600" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium text-sm block">Support Tickets</span>
                                    <span className="text-xs text-muted-foreground">
                                        {recentTickets.filter(t => t.status === 'open').length} open
                                    </span>
                                </div>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminDashboard;
